import {
  captureFriendInviteAnswerSaved,
  captureFriendInviteAnswerRequest,
} from '@/lib/friendInviteProgress';
import type { PostgrestError } from '@supabase/supabase-js';
import { isSupabaseTransportFailure } from '@/lib/utils/supabaseTransportFailure';
import { QuizRunPersistenceError } from './types';
import type { Json } from '@/types/database';
import { supabase } from '@/lib/supabase';
import { acquireActiveRunPersistenceWrite } from '@/lib/auth/persistenceWriteBarrier';
import { runSerializedSelfIdentityWrite } from '@/lib/auth/selfIdentityWriteSerialization';
import { toRpcIdentityParams } from '@/lib/auth/rpcIdentityParams';
import { mapPollResponseResult, type PollResponseResult } from '@/lib/services/pollResponses';
import type {
  CompletionRequest,
  ItemProgressWrite,
  PollWrite,
  QuizCompletion,
  QuizRunPersistenceTransport,
  TriviaWrite,
} from './types';

export type QuizRunPersistenceTransportOptions = {
  questionIndexForItem: (editionItemId: string) => number | undefined;
};

// The completion RPC requires positive client revisions. A hydrated legacy
// fact may legitimately carry revision zero, so promote it only at the
// finalizer boundary; progressive writes still retain their journal revision.
const completionRevision = (revision: number): number => Math.max(1, revision);

function acquireWriteLease(): { release: () => void } {
  const lease = acquireActiveRunPersistenceWrite();
  if (!lease) {
    throw new Error('Cannot persist Quiz run while user identity is resolving');
  }
  return lease;
}

async function withWriteLease<T>(
  identity: { userId: string; isAnonymous: boolean },
  operation: (requestWithInvite: ReturnType<typeof captureFriendInviteAnswerRequest>) => Promise<T>,
  savesAnswer = false
): Promise<T> {
  const notifySaved = captureFriendInviteAnswerSaved(identity);
  const requestWithInvite = captureFriendInviteAnswerRequest(identity);
  return runSerializedSelfIdentityWrite(identity, async () => {
    const lease = acquireWriteLease();
    try {
      const result = await operation(requestWithInvite);
      if (savesAnswer) notifySaved();
      return result;
    } finally {
      lease.release();
    }
  });
}

function throwIfRpcFailed(error: PostgrestError | null, status: number): void {
  if (!error) return;
  throw new QuizRunPersistenceError(error.message, {
    code: error.code,
    status,
    retryable: isSupabaseTransportFailure(error, status),
  });
}

async function writeTrivia(
  input: TriviaWrite,
  options: QuizRunPersistenceTransportOptions
): Promise<unknown> {
  const questionIndex = options.questionIndexForItem(input.snapshot.editionItemId);
  if (questionIndex === undefined) {
    throw new Error(`Quiz item ${input.snapshot.editionItemId} is not playable`);
  }

  return withWriteLease(
    input.identity,
    async (requestWithInvite) => {
      const { data, error, status } = await requestWithInvite(
        supabase.rpc('log_quiz_answer', {
          p_quiz_id: input.identity.quizId,
          p_edition_id: input.identity.editionId,
          p_edition_item_id: input.snapshot.editionItemId,
          p_question_index: questionIndex,
          p_guess_sequence: input.snapshot.guessSequence as unknown as Json,
          p_rating: input.snapshot.rating ?? undefined,
          p_feedback: input.snapshot.feedback ?? undefined,
          p_feedback_text: input.snapshot.feedbackText ?? undefined,
          ...toRpcIdentityParams({
            userId: input.identity.userId,
            isAnonymous: input.identity.isAnonymous,
          }),
          p_xp_earned: input.snapshot.xpEarned,
          p_persistence_revision: input.snapshot.revision,
        })
      );
      throwIfRpcFailed(error, status);
      return data;
    },
    input.snapshot.guessSequence.length > 0
  );
}

async function writeItemProgress(input: ItemProgressWrite): Promise<unknown> {
  return withWriteLease(
    input.identity,
    async (requestWithInvite) => {
      const { data, error, status } = await requestWithInvite(
        supabase.rpc('record_quiz_item_progress', {
          p_quiz_id: input.identity.quizId,
          p_edition_id: input.identity.editionId,
          p_edition_item_id: input.snapshot.editionItemId,
          p_guess_sequence: input.snapshot.guessSequence as unknown as Json,
          ...toRpcIdentityParams({
            userId: input.identity.userId,
            isAnonymous: input.identity.isAnonymous,
          }),
        })
      );
      throwIfRpcFailed(error, status);
      return data;
    },
    input.snapshot.guessSequence.length > 0
  );
}

async function writePoll(input: PollWrite): Promise<PollResponseResult> {
  return withWriteLease(
    input.identity,
    async (requestWithInvite) => {
      const { data, error, status } = await requestWithInvite(
        supabase.rpc('record_poll_response', {
          p_poll_definition_id: input.snapshot.pollDefinitionId,
          p_option_id: input.snapshot.optionId,
          p_quiz_id: input.identity.quizId,
          p_edition_id: input.identity.editionId,
          p_edition_item_id: input.snapshot.editionItemId,
          p_session_id: input.snapshot.sessionId ?? undefined,
          ...toRpcIdentityParams({
            userId: input.identity.userId,
            isAnonymous: input.identity.isAnonymous,
          }),
          p_persistence_revision: input.snapshot.revision,
        })
      );
      throwIfRpcFailed(error, status);
      return mapPollResponseResult(data);
    },
    true
  );
}

async function finalizeCompletion(input: CompletionRequest): Promise<QuizCompletion> {
  return withWriteLease(
    input.identity,
    async (requestWithInvite) => {
      const { data, error, status } = await requestWithInvite(
        supabase.rpc('finalize_quiz_completion', {
          p_quiz_id: input.identity.quizId,
          p_edition_id: input.identity.editionId,
          p_items: input.items.map((item) =>
            item.itemType === 'trivia'
              ? {
                  edition_item_id: item.editionItemId,
                  item_type: item.itemType,
                  revision: completionRevision(item.revision),
                  guess_sequence: item.guessSequence,
                  rating: item.rating ?? null,
                  feedback: item.feedback ?? null,
                  feedback_text: item.feedbackText ?? null,
                  xp_earned: item.xpEarned,
                }
              : {
                  edition_item_id: item.editionItemId,
                  item_type: item.itemType,
                  revision: completionRevision(item.revision),
                  poll_definition_id: item.pollDefinitionId,
                  option_id: item.optionId,
                  session_id: item.sessionId ?? null,
                }
          ) as unknown as Json,
          ...toRpcIdentityParams({
            userId: input.identity.userId,
            isAnonymous: input.identity.isAnonymous,
          }),
        })
      );
      throwIfRpcFailed(error, status);
      const row = Array.isArray(data) ? data[0] : data;
      if (!row || typeof row !== 'object') {
        throw new Error('Quiz completion RPC returned no completion');
      }
      const result = row as Record<string, unknown>;
      return {
        completionId: String(result.completion_id),
        quizId: String(result.quiz_id),
        editionId: String(result.edition_id),
        emojiResults: String(result.emoji_results ?? ''),
        accuracy: Number(result.accuracy ?? 0),
        totalQuestions: Number(result.total_questions ?? 0),
        xpEarned: Number(result.xp_earned ?? 0),
        longestStreak: Number(result.longest_streak ?? 0),
        createdAt: typeof result.created_at === 'string' ? result.created_at : undefined,
      };
    },
    input.items.length > 0
  );
}

/** Maps Quiz persistence operations to the revision-aware database boundary. */
export function createQuizRunPersistenceTransport(
  options: QuizRunPersistenceTransportOptions
): QuizRunPersistenceTransport {
  return {
    writeItemProgress,
    writeTrivia: (input) => writeTrivia(input, options),
    writePoll,
    finalizeCompletion,
  };
}
